if (!customElements.get('product-personalization')) {
  customElements.define(
    'product-personalization',
    class ProductPersonalization extends HTMLElement {
      constructor() {
        super();
        this.config = {};
        this.fieldsRoot = null;
        this.form = null;
        this.onSubmit = this.onSubmit.bind(this);
        this.onInputCount = this.onInputCount.bind(this);
        this.onFieldChange = this.onFieldChange.bind(this);
      }

      connectedCallback() {
        const configEl = this.querySelector('[data-personalization-config]');
        if (configEl) {
          try {
            this.config = JSON.parse(configEl.textContent);
          } catch (err) {
            this.config = {};
          }
        }
        this.fieldsRoot = this.querySelector('[data-fields]');
        this.driverNames = (this.dataset.driverOptions || '')
          .split('|')
          .map((n) => this.normalizeName(n))
          .filter(Boolean);
        this.sectionId = this.dataset.sectionId;
        this.formId = this.dataset.formId;
        this.form = document.getElementById(this.formId);

        this.cacheTemplate();
        this.bindCounters();
        this.bindValidationListeners();

        if (this.form) {
          this.form.addEventListener('submit', this.onSubmit, true);
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeOption = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => this.syncFromVariantPicker());
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, () => this.syncFromVariantPicker());
        }
      }

      disconnectedCallback() {
        if (this.unsubscribeOption) this.unsubscribeOption();
        if (this.unsubscribeVariant) this.unsubscribeVariant();
        if (this.form) this.form.removeEventListener('submit', this.onSubmit, true);
      }

      syncFromVariantPicker() {
        const count = this.readDriverValue();
        if (count == null) return;
        this.render(count);
      }

      readDriverValue() {
        const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
        if (!variantSelects) return null;

        if (!this.driverNames || this.driverNames.length === 0) return null;

        const fieldsets = variantSelects.querySelectorAll('fieldset');
        for (const fieldset of fieldsets) {
          const legend = fieldset.querySelector('legend');
          if (!legend) continue;
          const name = this.normalizeName(legend.textContent.replace(/:.*$/, ''));
          if (!this.driverNames.includes(name)) continue;
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (checked) return this.toInt(checked.value);
        }

        const dropdowns = variantSelects.querySelectorAll('.product-form__input--dropdown');
        for (const wrap of dropdowns) {
          const label = wrap.querySelector('label');
          if (!label) continue;
          if (!this.driverNames.includes(this.normalizeName(label.textContent))) continue;
          const select = wrap.querySelector('select');
          if (select) return this.toInt(select.value);
        }

        return null;
      }

      normalizeName(value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .replace(/œ/g, 'oe')
          .replace(/’/g, "'");
      }

      toInt(value) {
        const n = parseInt(value, 10);
        return Number.isFinite(n) && n > 0 ? n : null;
      }

      render(count) {
        if (!this.fieldsRoot) return;
        const existing = this.fieldsRoot.querySelectorAll('.product-personalization__field');
        const currentCount = existing.length;

        if (count === currentCount) return;

        if (count < currentCount) {
          for (let i = currentCount - 1; i >= count; i--) {
            existing[i].remove();
          }
          return;
        }

        for (let i = currentCount + 1; i <= count; i++) {
          const node = this.buildField(i);
          if (!node) break;
          this.fieldsRoot.appendChild(node);
        }
        this.bindCounters();
        this.bindValidationListeners();
      }

      cacheTemplate() {
        if (!this.fieldsRoot) return;
        const firstField = this.fieldsRoot.querySelector('.product-personalization__field');
        if (!firstField) return;
        this.fieldTemplate = firstField.cloneNode(true);
        this.fieldTemplate.classList.remove('is-invalid');
        this.fieldTemplate.querySelectorAll('input').forEach((el) => {
          el.value = '';
          delete el.dataset.counterBound;
          delete el.dataset.validationBound;
        });
        this.fieldTemplate.querySelectorAll('select').forEach((el) => {
          el.selectedIndex = 0;
          delete el.dataset.validationBound;
        });
        const error = this.fieldTemplate.querySelector('[data-error]');
        if (error) error.hidden = true;
      }

      buildField(index) {
        if (!this.fieldTemplate) return null;
        const clone = this.fieldTemplate.cloneNode(true);

        const label = this.config.label || '';
        const propertyName = `properties[${label} ${index}]`;
        const fieldId = `Personalization-${this.sectionId}-${index}`;

        clone.classList.remove('is-invalid');

        const labelEl = clone.querySelector('label');
        if (labelEl) {
          labelEl.setAttribute('for', fieldId);
          labelEl.textContent = `${label} ${index}`.trim();
        }

        const control = clone.querySelector('input, select');
        if (control) {
          control.id = fieldId;
          control.name = propertyName;
          delete control.dataset.validationBound;
          if (control.tagName === 'INPUT') {
            control.value = '';
          } else if (control.tagName === 'SELECT') {
            control.selectedIndex = 0;
          }
        }

        const counter = clone.querySelector('[data-counter]');
        if (counter && control && control.tagName === 'INPUT') {
          const max = control.dataset.maxChars || control.maxLength || 30;
          counter.textContent = `0/${max}`;
        }

        const error = clone.querySelector('[data-error]');
        if (error) error.hidden = true;

        return clone;
      }

      bindCounters() {
        if (!this.fieldsRoot) return;
        const inputs = this.fieldsRoot.querySelectorAll('input[type="text"][data-max-chars]');
        inputs.forEach((input) => {
          if (input.dataset.counterBound === 'true') return;
          input.dataset.counterBound = 'true';
          input.addEventListener('input', this.onInputCount);
          this.updateCounter(input);
        });
      }

      onInputCount(e) {
        this.updateCounter(e.currentTarget);
      }

      updateCounter(input) {
        const max = input.dataset.maxChars || input.maxLength || 30;
        const counter = input.parentElement && input.parentElement.querySelector('[data-counter]');
        if (counter) counter.textContent = `${input.value.length}/${max}`;
      }

      bindValidationListeners() {
        if (!this.fieldsRoot) return;
        const fields = this.fieldsRoot.querySelectorAll('input, select');
        fields.forEach((field) => {
          if (field.dataset.validationBound === 'true') return;
          field.dataset.validationBound = 'true';
          field.addEventListener('input', this.onFieldChange);
          field.addEventListener('change', this.onFieldChange);
          field.addEventListener('blur', this.onFieldChange);
        });
      }

      onFieldChange(e) {
        const field = e.currentTarget;
        if (field.checkValidity()) this.clearFieldError(field);
      }

      setFieldError(field) {
        const wrapper = field.closest('.product-personalization__field');
        if (!wrapper) return;
        wrapper.classList.add('is-invalid');
        const error = wrapper.querySelector('[data-error]');
        if (error) error.hidden = false;
      }

      clearFieldError(field) {
        const wrapper = field.closest('.product-personalization__field');
        if (!wrapper) return;
        wrapper.classList.remove('is-invalid');
        const error = wrapper.querySelector('[data-error]');
        if (error) error.hidden = true;
      }

      onSubmit(e) {
        if (!this.fieldsRoot) return;
        const fields = this.fieldsRoot.querySelectorAll('input, select');
        let firstInvalid = null;
        fields.forEach((field) => {
          if (field.checkValidity()) {
            this.clearFieldError(field);
          } else {
            this.setFieldError(field);
            if (!firstInvalid) firstInvalid = field;
          }
        });

        if (firstInvalid) {
          e.preventDefault();
          e.stopImmediatePropagation();
          e.stopPropagation();
          firstInvalid.focus();
        }
      }
    }
  );
}
